/**
 * Recent Days Table Component - Display last 3 days of production data
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Clock } from 'lucide-react';
import { useDailyProductionData } from '@/hooks/useProductionData';
import { ProductionFilters } from '@/types/production';
import { formatNumberDisplay } from '@/lib/utils';

interface RecentDaysTableProps {
  filters: ProductionFilters;
}

export const RecentDaysTable: React.FC<RecentDaysTableProps> = ({ filters }) => {
  // Fetch daily production data
  const {
    data: dailyData = [],
    isLoading,
    error
  } = useDailyProductionData(filters);

  // Get last 3 days sorted by date (newest first)
  const recentData = React.useMemo(() => {
    return [...dailyData]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 3);
  }, [dailyData]);

  // Calculate min/max for M³ values in each process
  const processM3Stats = React.useMemo(() => {
    if (recentData.length === 0) return {};
    
    const processes = ['soThe', 'tinhChe', 'lapRap', 'nguoi', 'son'] as const;
    const stats: Record<string, { min: number; max: number }> = {};
    
    processes.forEach(process => {
      const values = recentData
        .map(item => item.processes?.[process]?.volume)
        .filter((value): value is number => value !== null && value !== undefined && value > 0);
      
      if (values.length > 1) { // Only highlight if we have multiple values to compare
        stats[process] = {
          min: Math.min(...values),
          max: Math.max(...values)
        };
      }
    });
    
    return stats;
  }, [recentData]);

  // Format number with decimal places
  // Use Vietnamese locale formatting for numbers

  // Get conditional styling for M³ values
  const getM3Styling = (value: number | null, processName: string) => {
    if (!value || value <= 0 || !processM3Stats[processName]) {
      return 'text-sm text-center bg-amber-50 font-semibold border-r';
    }
    
    const { min, max } = processM3Stats[processName];
    if (value === max) {
      return 'text-sm text-center bg-green-600 text-white font-semibold border-r'; // Highest value: green background, white text
    } else if (value === min) {
      return 'text-sm text-center bg-red-600 text-white font-semibold border-r'; // Lowest value: red background, white text
    }
    
    return 'text-sm text-center bg-amber-50 font-semibold border-r'; // Default amber styling
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Ba ngày gần nhất
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Ba ngày gần nhất
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-red-600">
            <p>Lỗi tải dữ liệu: {error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-600" />
          🕒 Ba ngày gần nhất
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        {recentData.length > 0 ? (
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <Table>
              <TableHeader>
                {/* Mobile header - only M³ values */}
                <TableRow className="sm:hidden">
                  <TableHead className="text-center font-medium text-xs px-1 py-2 border-r min-w-[60px]">Ngày</TableHead>
                  <TableHead className="text-center bg-blue-50 text-xs px-1 py-2 min-w-[45px] border-r">Sơ chế</TableHead>
                  <TableHead className="text-center bg-green-50 text-xs px-1 py-2 min-w-[45px] border-r">Tinh chế</TableHead>
                  <TableHead className="text-center bg-yellow-50 text-xs px-1 py-2 min-w-[45px] border-r">Lắp ráp</TableHead>
                  <TableHead className="text-center bg-purple-50 text-xs px-1 py-2 min-w-[45px] border-r">Nguội</TableHead>
                  <TableHead className="text-center bg-orange-50 text-xs px-1 py-2 min-w-[45px]">Sơn</TableHead>
                </TableRow>
                
                {/* Desktop header - full layout */}
                <TableRow className="hidden sm:table-row">
                  <TableHead className="text-center font-medium w-[110px] border-r">Ngày</TableHead>
                  <TableHead colSpan={3} className="text-center border-r bg-blue-50">Sơ chế</TableHead>
                  <TableHead colSpan={3} className="text-center border-r bg-green-50">Tinh chế</TableHead>
                  <TableHead colSpan={3} className="text-center border-r bg-yellow-50">Lắp ráp</TableHead>
                  <TableHead colSpan={3} className="text-center border-r bg-purple-50">Nguội</TableHead>
                  <TableHead colSpan={3} className="text-center bg-orange-50">Sơn</TableHead>
                </TableRow>
                <TableRow className="hidden sm:table-row">
                  <TableHead></TableHead>
                  <TableHead className="text-xs text-center w-[55px] border-r">Công</TableHead>
                  <TableHead className="text-xs text-center w-[50px] bg-amber-100 font-semibold border-r">M³</TableHead>
                  <TableHead className="text-xs text-center w-[50px] border-r">NS</TableHead>
                  <TableHead className="text-xs text-center w-[55px] border-r">Công</TableHead>
                  <TableHead className="text-xs text-center w-[50px] bg-amber-100 font-semibold border-r">M³</TableHead>
                  <TableHead className="text-xs text-center w-[50px] border-r">NS</TableHead>
                  <TableHead className="text-xs text-center w-[55px] border-r">Công</TableHead>
                  <TableHead className="text-xs text-center w-[50px] bg-amber-100 font-semibold border-r">M³</TableHead>
                  <TableHead className="text-xs text-center w-[50px] border-r">NS</TableHead>
                  <TableHead className="text-xs text-center w-[55px] border-r">Công</TableHead>
                  <TableHead className="text-xs text-center w-[50px] bg-amber-100 font-semibold border-r">M³</TableHead>
                  <TableHead className="text-xs text-center w-[50px] border-r">NS</TableHead>
                  <TableHead className="text-xs text-center w-[55px] border-r">Công</TableHead>
                  <TableHead className="text-xs text-center w-[50px] bg-amber-100 font-semibold border-r">M³</TableHead>
                  <TableHead className="text-xs text-center w-[50px]">NS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentData.map((item, index) => (
                  <>
                    {/* Mobile row - only M³ values */}
                    <TableRow key={`${index}-mobile`} className="sm:hidden hover:bg-gray-50">
                      <TableCell className="font-medium text-center border-r text-xs px-1 py-2">
                        {new Date(item.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}
                      </TableCell>
                      <TableCell className={`${getM3Styling(item.processes?.soThe?.volume, 'soThe').replace('text-sm', 'text-xs').replace('sm:px-4', 'px-1')} py-2`}>
                        {formatNumberDisplay(item.processes?.soThe?.volume, 1)}
                      </TableCell>
                      <TableCell className={`${getM3Styling(item.processes?.tinhChe?.volume, 'tinhChe').replace('text-sm', 'text-xs').replace('sm:px-4', 'px-1')} py-2`}>
                        {formatNumberDisplay(item.processes?.tinhChe?.volume, 1)}
                      </TableCell>
                      <TableCell className={`${getM3Styling(item.processes?.lapRap?.volume, 'lapRap').replace('text-sm', 'text-xs').replace('sm:px-4', 'px-1')} py-2`}>
                        {formatNumberDisplay(item.processes?.lapRap?.volume, 1)}
                      </TableCell>
                      <TableCell className={`${getM3Styling(item.processes?.nguoi?.volume, 'nguoi').replace('text-sm', 'text-xs').replace('sm:px-4', 'px-1')} py-2`}>
                        {formatNumberDisplay(item.processes?.nguoi?.volume, 1)}
                      </TableCell>
                      <TableCell className={`${getM3Styling(item.processes?.son?.volume, 'son').replace('text-sm', 'text-xs').replace('sm:px-4', 'px-1').replace('border-r', '')} py-2`}>
                        {formatNumberDisplay(item.processes?.son?.volume, 1)}
                      </TableCell>
                    </TableRow>

                    {/* Desktop row - full layout */}
                    <TableRow key={`${index}-desktop`} className="hidden sm:table-row hover:bg-gray-50">
                      <TableCell className="font-medium text-center border-r">
                        {new Date(item.date).toLocaleDateString('vi-VN')}
                      </TableCell>
                      
                      {/* Sơ chế */}
                      <TableCell className="text-sm text-center border-r">{formatNumberDisplay(item.processes?.soThe?.labor || 0, 0)}</TableCell>
                      <TableCell className={getM3Styling(item.processes?.soThe?.volume, 'soThe')}>{formatNumberDisplay(item.processes?.soThe?.volume, 2)}</TableCell>
                      <TableCell className="text-sm text-center border-r">{formatNumberDisplay(item.processes?.soThe?.productivity, 3)}</TableCell>
                      
                      {/* Tinh chế */}
                      <TableCell className="text-sm text-center border-r">{formatNumberDisplay(item.processes?.tinhChe?.labor, 1)}</TableCell>
                      <TableCell className={getM3Styling(item.processes?.tinhChe?.volume, 'tinhChe')}>{formatNumberDisplay(item.processes?.tinhChe?.volume, 2)}</TableCell>
                      <TableCell className="text-sm text-center border-r">{formatNumberDisplay(item.processes?.tinhChe?.productivity, 3)}</TableCell>
                      
                      {/* Lắp ráp */}
                      <TableCell className="text-sm text-center border-r">{formatNumberDisplay(item.processes?.lapRap?.labor, 1)}</TableCell>
                      <TableCell className={getM3Styling(item.processes?.lapRap?.volume, 'lapRap')}>{formatNumberDisplay(item.processes?.lapRap?.volume, 2)}</TableCell>
                      <TableCell className="text-sm text-center border-r">{formatNumberDisplay(item.processes?.lapRap?.productivity, 3)}</TableCell>
                      
                      {/* Nguội */}
                      <TableCell className="text-sm text-center border-r">{formatNumberDisplay(item.processes?.nguoi?.labor, 1)}</TableCell>
                      <TableCell className={getM3Styling(item.processes?.nguoi?.volume, 'nguoi')}>{formatNumberDisplay(item.processes?.nguoi?.volume, 2)}</TableCell>
                      <TableCell className="text-sm text-center border-r">{formatNumberDisplay(item.processes?.nguoi?.productivity, 3)}</TableCell>
                      
                      {/* Sơn */}
                      <TableCell className="text-sm text-center border-r">{formatNumberDisplay(item.processes?.son?.labor, 1)}</TableCell>
                      <TableCell className={getM3Styling(item.processes?.son?.volume, 'son')}>{formatNumberDisplay(item.processes?.son?.volume, 2)}</TableCell>
                      <TableCell className="text-sm text-center">{formatNumberDisplay(item.processes?.son?.productivity, 3)}</TableCell>
                    </TableRow>
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Không có dữ liệu sản xuất</p>
            <p className="text-sm">Hãy thử điều chỉnh bộ lọc hoặc kiểm tra kết nối dữ liệu</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};